import {groupBy} from '@benchling/js-utils';
import {Button, Group, Stack, Text} from '@mantine/core';
import {useDisclosure} from '@mantine/hooks';
import React, {useState} from 'react';

import * as analytics from 'analytics/Analytics';
import {isAntibodySubSchemaRole} from 'antibodies/schema-configuration/AntibodyEnforcedDataModelConstants';
import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {ANTIBODIES_ANALYTICS_ACTIONS} from 'constants/analytics-products/AntibodiesAnalyticsConstants';
import {ClientTagSchemaConstraint} from 'constants/RegistryConstants';
import {
  AntibodyComponentSchemaRole,
  AntibodyEnforcedSchemaRole,
  AntibodyRelatedSchemaRole,
  FieldDataMigrationGroupStatus,
  FieldDefinitionType,
  TagSchemaItemType,
  TypeSpecificConstraintType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {useSkippableBenchlingQuery, QueryResultType} from 'graphql-client/BenchlingQuery';
import notifications from 'notifications';
import {ClientSchemaField} from 'registry/field/FieldTable';
import type {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {ImplementedStaticSchemaInterface} from 'schemas/SchemaConstants';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import Menu from 'ui-library/menus/Menu';
import {OptionData} from 'ui-library/menus/MenuConstants';
import SearchableMenu from 'ui-library/menus/SearchableMenu';
import {SettingsLabel} from 'ui-library/SettingsField';
import Spinner from 'ui-library/Spinner';

import {ActiveAntibodySchemaMigrationQuery} from './__generated/ActiveAntibodySchemaMigrationQuery';
import {AntibodyEnforcedDataModelSpecsQuery} from './__generated/AntibodyEnforcedDataModelSpecsQuery';
import {
  ACTIVE_ANTIBODY_SCHEMA_MIGRATION_QUERY,
  ActiveAntibodyMigration,
  ANTIBODY_ENFORCED_DATA_MODEL_SPECS_QUERY,
  AntibodyEnforcedSchemaSpec,
  AntibodyEnforcedConstraintSpec,
  getAntibodyEnforcedSchemaRoleFromSpec,
} from './AntibodyEnforcedDataModelConstants';
import {enforcedFieldSpecToField, removeAntibodyEnforcedFields} from './AntibodyEnforcedSchemaSpecUtils';
import {
  ANTIBODY_COMPONENT_SCHEMA_INTERFACE_ID_TO_DISPLAY_NAME,
  AntibodyComponentSchemaInterfaceApiId,
  isAntibodyComponentSchemaInterfaceId,
  ANTIBODY_COMPONENT_INTERFACE_ID_TO_SCHEMA_ROLE,
  ANTIBODY_ENFORCED_ROLE_TO_INTERFACE_ID,
  ANTIBODY_SCHEMA_ROLE_TO_INTERFACE_ID,
} from './AntibodySchemaInterfaceConstants';
import {AntibodyComponentMigrationReviewModal} from './schema-migration/AntibodyComponentMigrationReviewModal';
import {finalizeMigrateSchema} from './schema-migration/SchemaMigrationDAO';

export type AntibodyComponentSchemaOption =
  | typeof AntibodyComponentSchemaInterfaceApiId.AA_DOMAIN_ID
  | typeof AntibodyComponentSchemaInterfaceApiId.AA_SUB_ELEMENT_ID
  | typeof AntibodyComponentSchemaInterfaceApiId.AA_CHAIN_ID
  | typeof AntibodyComponentSchemaInterfaceApiId.DNA_DOMAIN_ID
  | typeof AntibodyComponentSchemaInterfaceApiId.DNA_SUB_ELEMENT_ID
  | typeof AntibodyComponentSchemaInterfaceApiId.DNA_CHAIN_ID
  | typeof AntibodyComponentSchemaInterfaceApiId.AA_PAIR_ID
  | typeof AntibodyComponentSchemaInterfaceApiId.DNA_PAIR_ID
  | typeof AntibodyComponentSchemaInterfaceApiId.ANTIGEN_ID
  | null;

export type AntibodyComponentSchemaItemType =
  | TagSchemaItemType.AA_SEQUENCE
  | TagSchemaItemType.DNA_SEQUENCE
  | TagSchemaItemType.BASIC_FOLDER_ITEM
  | TagSchemaItemType.MOLECULE;

export const ANTIBODY_COMPONENT_SCHEMA_ITEM_TYPES: ReadonlyArray<AntibodyComponentSchemaItemType> = [
  TagSchemaItemType.AA_SEQUENCE,
  TagSchemaItemType.DNA_SEQUENCE,
  TagSchemaItemType.BASIC_FOLDER_ITEM,
  TagSchemaItemType.MOLECULE,
];

export function isAntibodyComponentSchemaItemType(
  itemType: TagSchemaItemType | null
): itemType is AntibodyComponentSchemaItemType {
  return (
    itemType != null &&
    ANTIBODY_COMPONENT_SCHEMA_ITEM_TYPES.includes(itemType as AntibodyComponentSchemaItemType)
  );
}

export interface QueryingAntibodyComponentSchemaConfigurationProps {
  disabled: boolean;
  itemType: AntibodyComponentSchemaItemType;
  onChange: (value: AntibodyComponentSchemaOption, schemaSpec: AntibodyEnforcedSchemaSpec | null) => void;
  value: AntibodyComponentSchemaOption;
  /* Whether the schema is being newly created, or existing and being migrated */
  isNewSchema: boolean;
  /* ID of an existing schema — used to query for any ongoing antibody migration */
  schemaId: string | null;
  /* Called after a migration is successfully published, e.g. to refetch parent query data.
   * Required to enable the Publish button — if omitted, publishing is never surfaced to the user. */
  onPublishComplete?: () => Promise<void>;
}

export interface AntibodyComponentSchemaConfigurationProps {
  disabled: boolean;
  itemType: AntibodyComponentSchemaItemType;
  onChange: (value: AntibodyComponentSchemaOption, schemaSpec: AntibodyEnforcedSchemaSpec | null) => void;
  value: AntibodyComponentSchemaOption;
  isNewSchema: boolean;
  schemaSpecs: Array<AntibodyEnforcedSchemaSpec>;
  activeMigration: ActiveAntibodyMigration | null;
  schemaId: string | null;
  onPublish?: () => Promise<void>;
}

export const ITEM_TYPE_TO_INTERFACE_IDS: Record<
  AntibodyComponentSchemaItemType,
  Array<AntibodyComponentSchemaInterfaceApiId>
> = {
  [TagSchemaItemType.AA_SEQUENCE]: [
    AntibodyComponentSchemaInterfaceApiId.AA_DOMAIN_ID,
    AntibodyComponentSchemaInterfaceApiId.AA_SUB_ELEMENT_ID,
    AntibodyComponentSchemaInterfaceApiId.AA_CHAIN_ID,
    AntibodyComponentSchemaInterfaceApiId.ANTIGEN_ID,
  ],
  [TagSchemaItemType.DNA_SEQUENCE]: [
    AntibodyComponentSchemaInterfaceApiId.DNA_DOMAIN_ID,
    AntibodyComponentSchemaInterfaceApiId.DNA_SUB_ELEMENT_ID,
    AntibodyComponentSchemaInterfaceApiId.DNA_CHAIN_ID,
  ],
  [TagSchemaItemType.BASIC_FOLDER_ITEM]: [
    AntibodyComponentSchemaInterfaceApiId.AA_PAIR_ID,
    AntibodyComponentSchemaInterfaceApiId.DNA_PAIR_ID,
    AntibodyComponentSchemaInterfaceApiId.ANTIGEN_ID,
  ],
  [TagSchemaItemType.MOLECULE]: [AntibodyComponentSchemaInterfaceApiId.ANTIGEN_ID],
};

export function isAntibodyComponentSchemaInterfaceIdValidForItemType(
  interfaceId: string,
  itemType: TagSchemaItemType | null
): boolean {
  return (
    itemType != null &&
    isAntibodyComponentSchemaInterfaceId(interfaceId) &&
    isAntibodyComponentSchemaItemType(itemType) &&
    ITEM_TYPE_TO_INTERFACE_IDS[itemType].includes(interfaceId)
  );
}

function getOptionsForItemType(
  itemType: AntibodyComponentSchemaItemType,
  isNewSchema: boolean
): Array<OptionData> {
  if (!isNewSchema && BenchlingConfig.MOLBIO_ENABLE_ANTIBODY_IN_PLACE_MIGRATION) {
    // In-place migration only allowed for AA chain, AA domain (WIP), and antigen for now.
    const allowedInterfaceIds = [
      AntibodyComponentSchemaInterfaceApiId.AA_CHAIN_ID,
      AntibodyComponentSchemaInterfaceApiId.ANTIGEN_ID,
      AntibodyComponentSchemaInterfaceApiId.AA_DOMAIN_ID,
    ];
    return [
      {name: 'None', value: null},
      ...ITEM_TYPE_TO_INTERFACE_IDS[itemType].map((id) => ({
        name: ANTIBODY_COMPONENT_SCHEMA_INTERFACE_ID_TO_DISPLAY_NAME[id],
        value: id,
        disabled: !allowedInterfaceIds.includes(id),
      })),
    ];
  }
  return [
    {name: 'None', value: null},
    ...ITEM_TYPE_TO_INTERFACE_IDS[itemType].map((id) => ({
      name: ANTIBODY_COMPONENT_SCHEMA_INTERFACE_ID_TO_DISPLAY_NAME[id],
      value: id,
    })),
  ];
}

export function shouldRemoveAntibodyEnforcedFields(
  schemaId: string | null,
  itemType: TagSchemaItemType | null,
  interfaceData: Array<ImplementedStaticSchemaInterface>
): boolean {
  const maybeAntibodyComponentSchemaInterface = interfaceData.find((interfaceData) =>
    isAntibodyComponentSchemaInterfaceId(interfaceData.interfaceId)
  );
  return (
    // Only remove fields if we are creating a new schema - otherwise the fields might have data!
    schemaId == null &&
    // Only remove fields if we don't have an antibody component schema interface
    (maybeAntibodyComponentSchemaInterface == null ||
      // Or the interface is not valid for the item type
      !isAntibodyComponentSchemaInterfaceIdValidForItemType(
        maybeAntibodyComponentSchemaInterface.interfaceId,
        itemType
      ))
  );
}

function validateDropdownFieldSpecs(
  schemaSpec: AntibodyEnforcedSchemaSpec | null,
  registryId: string
): boolean {
  const enforcedFieldSpecs = schemaSpec?.enforcedFieldSpecs ?? [];
  const dropdownFieldSpecsWithoutCompatibleDropdowns = enforcedFieldSpecs.filter(
    (spec) =>
      spec.fieldType === FieldDefinitionType.DROPDOWN &&
      (spec.dropdown?.compatibleDropdowns == null ||
        spec.dropdown.compatibleDropdowns.length === 0 ||
        spec.dropdown.compatibleDropdowns.every((dropdown) => dropdown.registry.id !== registryId))
  );
  return dropdownFieldSpecsWithoutCompatibleDropdowns.length === 0;
}

export function constraintSpecToClientTagSchemaConstraint(
  constraintSpec: AntibodyEnforcedConstraintSpec,
  schemaFields: Array<ClientSchemaField | SchemaField>
): ClientTagSchemaConstraint {
  const fieldsByName: Record<string, Array<ClientSchemaField | SchemaField>> = groupBy(
    schemaFields,
    (field) => field.name
  );
  assert(
    constraintSpec.enforcedMinimumFieldDefinitionNames.every(
      (fieldName) => fieldsByName[fieldName] != null && fieldsByName[fieldName].length > 0
    ),
    'Expected all minimum unique fields to be present in the schema fields'
  );
  return {
    areUniqueResiduesCaseSensitive: constraintSpec.enforcedTypeSpecificConstraint?.isCaseSensitive ?? false,
    uniqueResidues:
      constraintSpec.enforcedTypeSpecificConstraint?.constraintType ===
      TypeSpecificConstraintType.UNIQUE_RESIDUES,
    uniqueCanonicalSmiles:
      constraintSpec.enforcedTypeSpecificConstraint?.constraintType ===
      TypeSpecificConstraintType.UNIQUE_CANONICAL_SMILES,
    fields: constraintSpec.enforcedMinimumFieldDefinitionNames.map((fieldName) => fieldsByName[fieldName][0]),
  };
}

/**
 * Clears any fields from the existing constraint that are not present in the updated schema fields.
 *
 * This is necessary because when we deselect an antibody component role, we need to clear any fields that
 * were added by that role from the constraint.
 */
export function clearInvalidFieldsFromExistingConstraint(
  existingConstraint: ClientTagSchemaConstraint,
  updatedSchemaFields: Array<ClientSchemaField>
): ClientTagSchemaConstraint {
  const validFieldNames = new Set(updatedSchemaFields.map((field) => field.name));
  return {
    ...existingConstraint,
    fields: existingConstraint.fields.filter((field) => validFieldNames.has(field.name)),
  };
}

function consolidateFieldsPreserveOrder(
  fields1: Array<ClientSchemaField>,
  fields2: Array<ClientSchemaField>
): Array<ClientSchemaField> {
  const fieldsByName: Record<string, Array<ClientSchemaField>> = groupBy(fields1, (field) => field.name);
  return [
    ...fields1,
    ...fields2.filter((field) => fieldsByName[field.name] == null || fieldsByName[field.name].length === 0),
  ];
}

function updateExistingConstraint(
  constraintSpec: AntibodyEnforcedConstraintSpec,
  existingConstraint: ClientTagSchemaConstraint,
  updatedSchemaFields: Array<ClientSchemaField>
): ClientTagSchemaConstraint {
  const speccedConstraint = constraintSpecToClientTagSchemaConstraint(constraintSpec, updatedSchemaFields);
  const cleanedExistingConstraint = clearInvalidFieldsFromExistingConstraint(
    existingConstraint,
    updatedSchemaFields
  );
  return {
    // For uniqueness, the spec always takes precedence.
    uniqueResidues: speccedConstraint.uniqueResidues,
    uniqueCanonicalSmiles: speccedConstraint.uniqueCanonicalSmiles,
    areUniqueResiduesCaseSensitive: speccedConstraint.areUniqueResiduesCaseSensitive,
    // We specify a minimum set of required constraint fields in the spec, so we merge the existing fields
    // with the specced fields.
    fields: consolidateFieldsPreserveOrder(
      cleanedExistingConstraint.fields as Array<ClientSchemaField>,
      speccedConstraint.fields as Array<ClientSchemaField>
    ),
  };
}

export function computeUpdatedConstraintForEnforcedDataModel(
  schemaSpec: AntibodyEnforcedSchemaSpec | null,
  existingConstraint: ClientTagSchemaConstraint | null,
  updatedSchemaFields: Array<ClientSchemaField>
): ClientTagSchemaConstraint | null {
  if (schemaSpec == null || schemaSpec.enforcedConstraintSpec == null) {
    // If we deselected an antibody component role, we need to clear any fields that were added by that role from the constraint.
    // Otherwise, leave it as is, since we don't have a good way of knowing whether the rest of the constraint
    // was added by us or by the user prior to selecting the role.
    if (existingConstraint != null) {
      return clearInvalidFieldsFromExistingConstraint(existingConstraint, updatedSchemaFields);
    }
    return null;
  }
  if (existingConstraint == null) {
    return constraintSpecToClientTagSchemaConstraint(schemaSpec.enforcedConstraintSpec, updatedSchemaFields);
  }
  return updateExistingConstraint(schemaSpec.enforcedConstraintSpec, existingConstraint, updatedSchemaFields);
}

export function computeUpdatedFieldsForEnforcedDataModel(
  schemaSpec: AntibodyEnforcedSchemaSpec | null,
  registryId: string,
  existingFields: Array<ClientSchemaField>
): Array<ClientSchemaField> {
  if (schemaSpec == null) {
    return removeAntibodyEnforcedFields(existingFields);
  }

  const schemaRole = getAntibodyEnforcedSchemaRoleFromSpec(schemaSpec);
  if (!isAntibodySubSchemaRole(schemaRole)) {
    // Antibody root schemas themselves do not currently show any enforced fields on create.
    return removeAntibodyEnforcedFields(existingFields);
  }
  const interfaceId = ANTIBODY_SCHEMA_ROLE_TO_INTERFACE_ID[schemaRole];

  if (!validateDropdownFieldSpecs(schemaSpec, registryId)) {
    notifications.error(
      `Tried to automatically add schema fields for '${ANTIBODY_COMPONENT_SCHEMA_INTERFACE_ID_TO_DISPLAY_NAME[interfaceId]}', but could not find all the prerequisite dropdown definitions in this registry. Please ask your tenant admin to run the 'set_up_enforced_protein_definitions' script for registry ID '${registryId}', then refresh this page and try again.`
    );
    return removeAntibodyEnforcedFields(existingFields);
  }

  const enforcedFieldReservedNames = new Set(schemaSpec.enforcedFieldSpecs.map((spec) => spec.fieldName));
  const enforcedFieldReservedSystemNames = new Set(
    schemaSpec.enforcedFieldSpecs.map((spec) => spec.systemName)
  );

  const existingFieldsWithoutConflictsOrEnforcedFields = removeAntibodyEnforcedFields(
    existingFields.filter(
      (field) =>
        !enforcedFieldReservedNames.has(field.name) && !enforcedFieldReservedSystemNames.has(field.systemName)
    )
  );

  // Computed field creation is handled separately on the backend, so computed enforced fields are
  // intentionally excluded from both the fields preview and the create args for new schemas.
  const enforcedFieldsToPrepend = schemaSpec.enforcedFieldSpecs
    .filter((enforcedFieldSpec) => enforcedFieldSpec.computedFieldSpec == null)
    .map((enforcedFieldSpec) => enforcedFieldSpecToField(interfaceId, enforcedFieldSpec, registryId));

  return [...enforcedFieldsToPrepend, ...existingFieldsWithoutConflictsOrEnforcedFields];
}

function getMigrationDisplayState(
  activeMigration: ActiveAntibodyMigration | null,
  value: AntibodyComponentSchemaOption
): {isDropdownDisabled: boolean; displayName: React.ReactNode} {
  const status = activeMigration?.fieldMigrationStatus ?? null;

  const migrationTargetInterfaceId =
    activeMigration != null ? ANTIBODY_ENFORCED_ROLE_TO_INTERFACE_ID[activeMigration.antibodyRole] : null;
  const migrationTargetName =
    migrationTargetInterfaceId != null
      ? ANTIBODY_COMPONENT_SCHEMA_INTERFACE_ID_TO_DISPLAY_NAME[migrationTargetInterfaceId]
      : null;

  const baseDisplayName =
    migrationTargetName != null
      ? migrationTargetName
      : value != null
        ? ANTIBODY_COMPONENT_SCHEMA_INTERFACE_ID_TO_DISPLAY_NAME[value]
        : 'None';

  if (status === FieldDataMigrationGroupStatus.PENDING && migrationTargetName != null) {
    return {
      isDropdownDisabled: true,
      displayName: (
        <>
          {migrationTargetName}
          <Text span={true}>{' · Migration in progress'}</Text>
        </>
      ),
    };
  }

  if (status === FieldDataMigrationGroupStatus.SUCCEEDED && migrationTargetName != null) {
    return {
      isDropdownDisabled: true,
      displayName: (
        <>
          {baseDisplayName}
          <Text span={true}>{' · Ready to publish'}</Text>
        </>
      ),
    };
  }

  return {
    isDropdownDisabled: false,
    displayName: baseDisplayName,
  };
}

/**
 * Dropdown component for configuring antibody component schema interfaces.
 * Displays options for selecting the antibody component type (e.g., AA domain,
 * AA chain, DNA domain, DNA chain, AA variable pair, DNA variable pair) based on the item type.
 */
export function AntibodyComponentSchemaConfiguration({
  disabled,
  itemType,
  onChange,
  value,
  schemaSpecs,
  isNewSchema,
  activeMigration,
  schemaId,
  onPublish,
}: AntibodyComponentSchemaConfigurationProps): JSX.Element {
  const handleSelect = (selectedValue: AntibodyComponentSchemaOption): void => {
    analytics.trackEvent(ANTIBODIES_ANALYTICS_ACTIONS.SELECTED_ANTIBODY_COMPONENT_SSI_ON_SCHEMA, {
      componentType: selectedValue,
      itemType,
    });
    if (selectedValue == null) {
      onChange(null, null);
      return;
    }
    const expectedRole = ANTIBODY_COMPONENT_INTERFACE_ID_TO_SCHEMA_ROLE[selectedValue];
    const schemaSpec =
      schemaSpecs.find((spec) => getAntibodyEnforcedSchemaRoleFromSpec(spec) === expectedRole) ?? null;
    onChange(selectedValue, schemaSpec);
  };

  const options = getOptionsForItemType(itemType, isNewSchema);

  const {isDropdownDisabled, displayName} = getMigrationDisplayState(activeMigration, value);

  const [reviewOpened, {open: openReview, close: closeReview}] = useDisclosure(false);
  const [isPublishing, setIsPublishing] = useState(false);

  const handlePublish = async (): Promise<void> => {
    if (onPublish == null) {
      return;
    }
    setIsPublishing(true);
    try {
      await onPublish();
      closeReview();
    } finally {
      setIsPublishing(false);
    }
  };

  return (
    <Stack spacing="xs" style={{width: '50%'}}>
      <SettingsLabel label="Protein component type" />
      <Group spacing="xs" noWrap={true}>
        <SelectDropdown
          menu={
            <SearchableMenu
              optionDatas={options}
              onSelect={handleSelect}
              selectedValue={value}
              width={Menu.WIDTHS.MEDIUM}
            />
          }
          displayName={displayName}
          disabled={disabled || isDropdownDisabled}
          alwaysDropDown={true}
          dataTestAttrs={{
            component: 'AntibodyComponentSchemaConfiguration',
            element: 'dropdown',
          }}
        />
        {activeMigration?.fieldMigrationStatus === FieldDataMigrationGroupStatus.SUCCEEDED &&
          onPublish != null && (
            <Button size="xs" variant="secondary" onClick={openReview}>
              Review & publish
            </Button>
          )}
      </Group>
      {reviewOpened && onPublish != null && activeMigration != null && schemaId != null && (
        <AntibodyComponentMigrationReviewModal
          schemaId={schemaId}
          fieldDataMigrations={activeMigration.fieldDataMigrations}
          onClose={closeReview}
          onPublish={handlePublish}
          isPublishing={isPublishing}
        />
      )}
    </Stack>
  );
}

export default function QueryingAntibodyComponentSchemaConfiguration({
  disabled,
  itemType,
  onChange,
  value,
  isNewSchema,
  schemaId,
  onPublishComplete,
}: QueryingAntibodyComponentSchemaConfigurationProps): JSX.Element {
  const enforcedSchemaSpecsResult = useSkippableBenchlingQuery<AntibodyEnforcedDataModelSpecsQuery>(
    ANTIBODY_ENFORCED_DATA_MODEL_SPECS_QUERY,
    {
      skip: disabled,
    }
  );
  const activeMigrationResult = useSkippableBenchlingQuery<ActiveAntibodySchemaMigrationQuery>(
    ACTIVE_ANTIBODY_SCHEMA_MIGRATION_QUERY,
    {
      skip: schemaId == null,
      variables: {schemaId: schemaId!},
    }
  );
  if (
    enforcedSchemaSpecsResult.type === QueryResultType.PENDING ||
    activeMigrationResult.type === QueryResultType.PENDING
  ) {
    return <Spinner />;
  }
  if (enforcedSchemaSpecsResult.type === QueryResultType.ERROR) {
    throw new Error(
      `Error querying protein component schema configuration: ${enforcedSchemaSpecsResult.error}`
    );
  }
  if (activeMigrationResult.type === QueryResultType.ERROR) {
    throw new Error(`Error querying active antibody schema migration: ${activeMigrationResult.error}`);
  }
  const enforcedSchemaSpecs =
    enforcedSchemaSpecsResult.type === QueryResultType.SKIPPED
      ? []
      : enforcedSchemaSpecsResult.data.antibodyEnforcedSchemaSpecs;
  const activeMigration =
    activeMigrationResult.type === QueryResultType.SKIPPED
      ? null
      : activeMigrationResult.data.activeAntibodySchemaMigration;

  const onPublish =
    schemaId != null &&
    activeMigration != null &&
    activeMigration.antibodyRole !== AntibodyEnforcedSchemaRole.ANTIBODY &&
    onPublishComplete != null
      ? async () => {
          await finalizeMigrateSchema({
            schemaId,
            antibodyRole: activeMigration.antibodyRole as unknown as
              | AntibodyComponentSchemaRole
              | AntibodyRelatedSchemaRole,
          });
          await onPublishComplete();
        }
      : undefined;

  return (
    <AntibodyComponentSchemaConfiguration
      activeMigration={activeMigration}
      isNewSchema={isNewSchema}
      disabled={disabled}
      itemType={itemType}
      onChange={onChange}
      onPublish={onPublish}
      schemaId={schemaId}
      schemaSpecs={enforcedSchemaSpecs}
      value={value}
    />
  );
}
